import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatBRL } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Eye } from "lucide-react";

const STATUSES = ["received", "payment_pending", "paid", "in_preparation", "ready_for_pickup", "completed", "cancelled"];
const STATUS_LABEL = {
  received: "Recebido", payment_pending: "Aguardando pagamento", paid: "Pago",
  in_preparation: "Em separação", ready_for_pickup: "Pronto p/ retirada",
  completed: "Concluído", cancelled: "Cancelado",
};
const PAYMENT_LABEL = { pending: "Pendente", paid: "Pago", failed: "Falhou", approved: "Aprovado", refunded: "Estornado" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/orders/${id}`);
      setDetail(data);
    } catch (e) { toast.error("Erro ao abrir pedido"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success("Status atualizado");
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    }
  };
  const changePayment = async (id, payment_status) => {
    try {
      await api.put(`/admin/orders/${id}`, { payment_status });
      toast.success("Pagamento atualizado");
      load();
      if (detail?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Pedidos</h1>
      <div className="bg-white border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-neutral-50 text-left">
            <tr><th className="p-3">Pedido</th><th className="p-3">Cliente</th><th className="p-3">Total</th><th className="p-3">Pagamento</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-t" data-testid={`order-row-${o.id}`}>
                <td className="p-3">
                  <button
                    onClick={() => openDetail(o.id)}
                    data-testid={`open-order-${o.id}`}
                    className="font-semibold text-[#C91F1F] hover:underline text-left"
                  >
                    #{o.order_number}
                  </button>
                  <p className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleString("pt-BR")}</p>
                </td>
                <td className="p-3"><p>{o.customer?.name}</p><p className="text-xs text-neutral-500">{o.customer?.phone}</p></td>
                <td className="p-3 font-semibold">{formatBRL(o.total)}</td>
                <td className="p-3">
                  <Select value={o.payment_status} onValueChange={(v) => changePayment(o.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`payment-select-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="paid">Pago</SelectItem>
                      <SelectItem value="failed">Falhou</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <Select value={o.status} onValueChange={(v) => changeStatus(o.id, v)}>
                    <SelectTrigger className="w-48" data-testid={`status-select-${o.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3">
                  <Button size="sm" variant="outline" onClick={() => openDetail(o.id)} data-testid={`view-order-${o.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Detalhes
                  </Button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-neutral-500">Nenhum pedido ainda.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
          <DialogHeader>
            <DialogTitle>Pedido #{detail?.order_number}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid sm:grid-cols-2 gap-3 bg-neutral-50 rounded-lg p-3">
                <div>
                  <p className="text-xs text-neutral-500 uppercase font-semibold">Cliente</p>
                  <p className="font-medium">{detail.customer?.name}</p>
                  <p className="text-neutral-600">{detail.customer?.email}</p>
                  <p className="text-neutral-600">{detail.customer?.phone}</p>
                  {detail.customer?.cpf && <p className="text-neutral-600">CPF: {detail.customer.cpf}</p>}
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase font-semibold">Pedido</p>
                  <p>Data: {new Date(detail.created_at).toLocaleString("pt-BR")}</p>
                  <p>Retirada: {detail.pickup_location}</p>
                  <p>Pagamento: <strong>{detail.payment_method?.toUpperCase()}</strong> — {PAYMENT_LABEL[detail.payment_status] || detail.payment_status}</p>
                  <p>Status: <strong>{STATUS_LABEL[detail.status] || detail.status}</strong></p>
                  {detail.stock_deducted && <p className="text-green-700 text-xs">Estoque já baixado</p>}
                </div>
              </div>

              <div>
                <p className="text-xs text-neutral-500 uppercase font-semibold mb-2">Itens</p>
                <div className="space-y-2">
                  {detail.items.map((i, idx) => (
                    <div key={idx} className="flex gap-3 border rounded-lg p-2">
                      <img src={i.image} className="w-14 h-16 rounded object-cover" alt="" />
                      <div className="flex-1">
                        <p className="font-medium">{i.name}</p>
                        <p className="text-xs text-neutral-500">SKU: {i.sku} • {[i.size, i.color].filter(Boolean).join(" • ") || "-"}</p>
                        <p className="text-xs text-neutral-500">Qtd {i.quantity} × {formatBRL(i.price)}</p>
                      </div>
                      <span className="font-semibold self-center">{formatBRL(i.line_total)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatBRL(detail.subtotal)}</span></div>
                {detail.discount > 0 && (
                  <div className="flex justify-between text-green-700"><span>Desconto {detail.coupon && `(${detail.coupon})`}</span><span>-{formatBRL(detail.discount)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-[#C91F1F]">{formatBRL(detail.total)}</span></div>
              </div>

              {detail.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                  <strong>Observações do cliente:</strong> {detail.notes}
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3 pt-3 border-t">
                <div>
                  <p className="text-xs font-semibold uppercase mb-1">Pagamento</p>
                  <Select value={detail.payment_status} onValueChange={(v) => changePayment(detail.id, v)}>
                    <SelectTrigger data-testid="detail-payment-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="paid">Pago</SelectItem>
                      <SelectItem value="failed">Falhou</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase mb-1">Status</p>
                  <Select value={detail.status} onValueChange={(v) => changeStatus(detail.id, v)}>
                    <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
